<script setup lang="ts">
//#region import
import { ref, computed } from 'vue';
import { useTypedStore } from '@/store';
import LanguagesSetup from '@/components/UIElements/LanguagesSetup.vue';
import type { GetAffiliateNotificationsResponse, GetLanguagesResponse } from '@/types/openapi-ts';
import type { EmitsToListeners } from '@/types/core';
//#endregion

//#region types
interface Props {
  popupData: GetAffiliateNotificationsResponse['payload'][number]
}

interface  Emits {
  'close': []
}

export interface ViewNotificationParams {
  data?: Props;
  listeners?: EmitsToListeners<Emits>
}
//#endregion

//#region props
const props = defineProps<Props>();
//#endregion

//#region emits
const emit = defineEmits<Emits>();
//#endregion

//#region composables
const store = useTypedStore();
//#endregion

//#region state
const isVisible = ref(false);
const index = ref(0);
//#endregion

//#region computed
const customerLanguages = computed(() => store.getters['misc/customerLanguages']);

const languages = computed(() => {
  let translations: GetLanguagesResponse['payload'] = [];

  if(props.popupData.translations) {
    translations = props.popupData.translations.map((translation) => {
      return customerLanguages.value.find((el) => el.code === translation.language_code)!;
    });
  }

  return [{
    name: 'English',
    name_native: 'English',
    language_code: 'en',
    is_default: true,
  }, ...translations];
});

const translations = computed(() => {
  if (index.value === 0) {
    return { title: props.popupData?.title, message: props.popupData?.message };
  }

  return props.popupData?.translations?.[index.value - 1];
});
//#endregion

//#region methods
const open = () => {
  isVisible.value = true;
};

const handleClose = () => {
  emit('close');
  isVisible.value = false;
};
function handleSelectTab(id: number) {
  index.value = id;
}
//#endregion

defineExpose({
  open,
  handleClose,
});
</script>

<template>
  <Popup
    v-model:is-visible="isVisible"
    class="view-notifications-popup"
    :title="$t('affiliates.notifications.popup.notification')"
    :width="600"
    @close="handleClose"
  >
    <template v-slot:body>
      <div class="t:-my-16 ">
        <LanguagesSetup
          class="t:-mx-24"
          :can-edit="false"
          :selected-index="index"
          :languages="languages"
          @select-index="handleSelectTab"
        >
          <div class="t:space-y-8">
            <div class="view-notifications-popup__date">
              {{ $filters.formatDate3(popupData.created_at!) }}
            </div>
            <div class="view-notifications-popup__title">
              {{ translations?.title }}
            </div>
            <div class="view-notifications-popup__description">
              {{ translations?.message }}
            </div>
          </div>
        </LanguagesSetup>
      </div>
    </template>
    <template v-slot:footer>
      <div class="t:justify-end t:flex t:space-x-10">
        <UiButton
          filled
          icon="check"
          size="big"
          @click="handleClose"
        >
          {{ $t('crm.buttons.close') }}
        </UiButton>
      </div>
    </template>
  </Popup>
</template>

<style lang="scss">
.view-notifications-popup{
  &__date {
    font-size: 12px;
    font-weight: 500;
    color: var(--text-body-secondary);
  }
  &__title {
    font-size: 16px;
    font-weight: 500;
    white-space: pre-line;
  }
  &__description {
    font-size: 13px;
    white-space: pre-line;
  }

  .popup__header{
    border: none;
  }
}
</style>
